import { useCallback, useMemo } from 'react';
import { useGetAccountProjectCountQuery } from '@frameio/graphql-client/generated';
import { useAccountPermissions } from '@frameio/permissions';
import { useActiveAccountId } from '../../../contexts';
import { useGetAccountStorageInfoQuery } from '../gql/generated/GetAccountStorageInfo';

export type AccountStorageInfo = {
  archivedLimitBytes?: number | null;
  archivedProjectsCount: number;
  archivedUsedBytes: number;
  canManageRetailStorage: boolean;
  isLoading: boolean;
  isTrialActive: boolean;
  mountedAddOnBytes: number;
  mountedEnabledOnAccount: boolean;
  mountedFeatureDisabled: boolean;
  mountedLimitBytes?: number | null;
  mountedPlanLimitBytes: number;
  mountedTrialLimitBytes: number;
  mountedProjectsCount: number;
  mountedUsedBytes: number;
  shouldDisableUpgradeToMountedStorage: (additionalBytes: number) => boolean;
  standardAddOnBytes: number;
  standardIncrementBytes: number;
  standardIncrementSeats: number;
  standardLimitBytes?: number | null;
  standardPlanLimitBytes: number;
  standardProjectsCount: number;
  standardUsedBytes: number;
};

export function useAccountStorageInfo(): AccountStorageInfo {
  const accountId = useActiveAccountId() || '';

  const { data, loading } = useGetAccountStorageInfoQuery({
    variables: { accountId },
    skip: !accountId,
  });

  const { data: projectCountData, loading: projectCountLoading } =
    useGetAccountProjectCountQuery({
      variables: {
        accountId,
        page: { first: 0 },
        projectStatus: ['INACTIVE'],
        storageTypes: [],
      },
      skip: !accountId,
    });

  const archivedProjectsCount =
    projectCountData?.account?.projects?.totalCount ?? 0;

  const canManageBilling = useAccountPermissions({
    accountId,
    check: 'canManageBilling',
  });

  const subscription = data?.account?.subscription;
  const limitedResources = subscription?.limitedResources;
  const usage = data?.account?.usage;
  const archivedLimitBytes = limitedResources?.archivedStorageBytes?.limit53;
  const archivedUsedBytes = limitedResources?.archivedStorageBytes?.used53 ?? 0;
  const mountedLimitBytes = limitedResources?.mountedStorageBytes?.limit53;
  const mountedPlanLimitBytes = subscription?.plan?.mountedStorageLimit ?? 0;
  const mountedTrialLimitBytes =
    subscription?.plan?.mountedStorageTrialLimit ?? 0;
  const mountedUsedBytes = limitedResources?.mountedStorageBytes?.used53 ?? 0;
  const standardLimitBytes = limitedResources?.storageBytes?.limit53; // includes plan, addOns, and perUserStorageIncrements. ("The limit/cap/maximum allowed by the Account's subscription/plan. Null if unlimited.")
  const standardPlanLimitBytes = subscription?.plan?.activeStorageLimit ?? 0; // base standard storage included in the plan (entitlement)
  const mountedAddOnBytes = useMemo(() => {
    return (
      subscription?.addOns?.filter(
        (addOn) => addOn.modifier === 'mounted_storage_limit'
      ) ?? []
    ).reduce((total, addOn) => total + addOn.increment * addOn.quantity, 0);
  }, [subscription?.addOns]);

  const standardAddOnBytes = useMemo(() => {
    // increment: The amount to increment the modifier for one quantity of the add-on (bytes)
    // quantity: The quantity of the add-on that has been added to the subscription
    return (
      subscription?.addOns?.filter(
        (addOn) => addOn.modifier === 'storage_limit'
      ) ?? []
    ).reduce((total, addOn) => total + addOn.increment * addOn.quantity, 0);
  }, [subscription?.addOns]);

  const shouldDisableUpgradeToMountedStorage = useCallback(
    (additionalBytes: number): boolean => {
      if (subscription?.tier === 'ENTERPRISE') {
        return false;
      }
      return (
        loading ||
        (mountedLimitBytes != null &&
          additionalBytes + mountedUsedBytes > (mountedLimitBytes ?? 0))
      );
    },
    [loading, mountedLimitBytes, mountedUsedBytes, subscription]
  );

  const isEnterprise = subscription?.tier === 'ENTERPRISE';
  const isFree = subscription?.tier === 'FREE';
  const isTrialActive = !!subscription?.trial?.isActive;
  const canManageRetailStorage =
    !isFree &&
    !isEnterprise &&
    !isTrialActive &&
    canManageBilling &&
    !subscription?.subscriptionEndAt;

  return {
    archivedLimitBytes,
    archivedProjectsCount,
    archivedUsedBytes,
    canManageRetailStorage,
    isLoading: loading || projectCountLoading,
    isTrialActive,
    mountedAddOnBytes,
    // if mountedFeatureDisabled is true, then mountedEnabledOnAccount will be false (automatically from BE)
    mountedEnabledOnAccount: data?.account?.mountedStorageEnabled ?? false,
    mountedFeatureDisabled:
      data?.account?.disabledFeatures?.mountedStorage ?? false,
    mountedLimitBytes,
    mountedPlanLimitBytes,
    mountedTrialLimitBytes,
    mountedProjectsCount: usage?.mountedStorage?.projectCount ?? 0,
    mountedUsedBytes,
    shouldDisableUpgradeToMountedStorage,
    standardAddOnBytes,
    standardIncrementBytes: subscription?.perUserStorage?.increment ?? 0,
    standardIncrementSeats: subscription?.perUserStorage?.seats ?? 0,
    standardLimitBytes,
    standardPlanLimitBytes,
    // usage.standardStorage.projectCount includes archived (inactive) projects
    standardProjectsCount:
      (usage?.standardStorage?.projectCount ?? 0) - archivedProjectsCount,
    standardUsedBytes: limitedResources?.storageBytes?.used53 ?? 0,
  };
}
